import React from 'react';
import { MapPin, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import type { CustomerAddress } from '../../types/user.types';

interface AddressListProps {
    addresses: CustomerAddress[];
    type: 'shipping' | 'billing';
    onEdit: (address: CustomerAddress) => void;
    onDelete: (addressId: string, type: 'shipping' | 'billing') => void;
    onAdd: () => void;
    isAdding: boolean;
}

export const AddressList: React.FC<AddressListProps> = ({
    addresses,
    type,
    onEdit,
    onDelete,
    onAdd,
    isAdding,
}) => {
    if (addresses.length === 0 && !isAdding) {
        return (
            <div className="text-center py-6 bg-gray-50/50 dark:bg-gray-900/50 rounded-2xl border border-dashed border-border/60">
                <MapPin className="size-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium text-gray-400">No {type} addresses</p>
                <Button
                    variant="link"
                    onClick={onAdd}
                    className="text-primary font-black text-xs uppercase tracking-widest mt-1 hover:underline"
                >
                    Add Address
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {addresses.map((address) => (
                <div key={address.id} className="p-5 bg-white dark:bg-black rounded-2xl border border-border/40 relative group shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <MapPin className="size-4 text-gray-400" />
                            <h4 className="font-black text-sm uppercase tracking-tight text-[#111418] dark:text-white">
                                {address.label || 'Address'}
                            </h4>
                        </div>
                        {address.is_default && (
                            <span className="text-[10px] font-black uppercase tracking-widest bg-blue-50 text-blue-500 dark:bg-blue-500/10 px-2 py-0.5 rounded-md">
                                Default
                            </span>
                        )}
                    </div>

                    <div className="space-y-1 text-sm text-gray-500 font-medium">
                        {address.name && <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">{address.name}</p>}
                        {address.phone && <p>{address.phone}</p>}
                        <p>{address.line1}</p>
                        {address.line2 && <p>{address.line2}</p>}
                        <p>{address.city}, {address.state} {address.postal_code}</p>
                        <p>{address.country}</p>
                    </div>

                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border/40">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onEdit(address)}
                            className="flex-1 h-9 rounded-xl font-black text-[10px] uppercase tracking-widest border-border/60"
                        >
                            <Edit2 className="size-3 mr-1.5" /> Edit
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onDelete(address.id, type)}
                            className="h-9 w-10 min-w-10 rounded-xl font-black text-red-500 border-border/60 hover:bg-red-50 dark:hover:bg-red-500/10"
                        >
                            <Trash2 className="size-3" />
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    );
};
